import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  forwardRef,
  OnInit
} from '@angular/core';
import { Carrier, StateCountry, ExtendedStorage } from 'src/app/shared/common-components/common-structure';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  Validators,
  ReactiveFormsModule
} from '@angular/forms';
import {BehaviorSubject, Observable} from 'rxjs';
import {filter, takeWhile} from 'rxjs/operators';
import {BsDatepickerModule} from "ngx-bootstrap/datepicker";
import {NgClass, NgFor, NgIf} from "@angular/common";
import moment from 'moment';
import {CommonUtils} from "../../../shared/common-components/common-utils";
import {XTOSReportingService} from "../../../services/XTOS-reporting/xtos-reporting.service";
import {RatesSroMasterService} from "../../../services/rates-sro-master/rates-sro-master.service";
import {TypeaheadMatch, TypeaheadModule} from "ngx-bootstrap/typeahead";
import {CommonValidators} from "../../../shared/common-components/common-validators";

@Component({
    selector: 'app-xtos-shipment',
    templateUrl: './xtos-shipment.component.html',
    styleUrls: ['./xtos-shipment.component.css'],
    imports: [
        ReactiveFormsModule, BsDatepickerModule, NgIf, NgFor, TypeaheadModule]
})
export class XtosShipmentComponent implements OnInit, OnChanges, OnDestroy {

  @Input() carriers: Carrier[];
  @Input() states: StateCountry[];
  @Input() xtosShipment: ExtendedStorage;
  @Input() isXTOSShipmentSelected: boolean;
  @Output() saveXTOSShipment = new EventEmitter<ExtendedStorage>();
  @Output() deleteXTOSShipment = new EventEmitter<ExtendedStorage>();
  maxDate = new Date();
  xtosShipmentForm: UntypedFormGroup;
  storageTypes: any = [
    'Extended Storage',
    'Climate Control'
  ];
  sroCodes = [];
  dataSourceOrigin: Observable<any>;
  typeaheadOriginLoading: boolean;
  qtrList: any = [{
    "value": -1,
    "name": "All Quarters"
  },{
    "value": 1,
    "name": "Quarter 1 (January - March)"
  },
    {
      "value": 2,
      "name": "Quarter 2 (April - June)"
    },
    {
      "value": 3,
      "name": "Quarter 3 (July - September)"
    },
    {
      "value": 4,
      "name": "Quarter 4 (October - December)"
    }];

  printQtrList:any =[];
  private observer$ = new BehaviorSubject(true);
  packingDateError = 'Date into Extended Storage should be on or after pick up date.';
  showPackingDateError = false;
  billingDateError = 'Billing date should be on or after pick up date';
  showBillingDateError = false;

  constructor(private formBuilder: UntypedFormBuilder, private xtosReportingService: XTOSReportingService,
              private ratesSroMasterService: RatesSroMasterService) {
    this.xtosShipmentForm = this.formBuilder.group({
      hhgExtStorageShipmentId: new UntypedFormControl(),
      carrierId: new UntypedFormControl(null, [Validators.required]),
      storageStateId: new UntypedFormControl(null, [Validators.required]),
      referenceNumber: new UntypedFormControl('', [Validators.required]),
      shipmentWeight: new UntypedFormControl('', [Validators.required, Validators.min(1)]),
      packingDate: new UntypedFormControl('', [Validators.required]),
      billingDate: new UntypedFormControl('', [Validators.required]),
      bolNumber: new UntypedFormControl('', [Validators.required]),
      storageStreetAddr: new UntypedFormControl('', [Validators.required]),
      storageZipId: new UntypedFormControl('', [Validators.required]),
      uspsCity: new UntypedFormControl(''),
      city: new UntypedFormControl('',[Validators.maxLength(45)]),
      state: new UntypedFormControl({ value: '', disabled: true }),
      stateId: new UntypedFormControl(''),
      zipCode: new UntypedFormControl('', [Validators.maxLength(6), CommonValidators.numeric]),
      passThruAmount: new UntypedFormControl(''),
      dateIntoExtraStorage: new UntypedFormControl('', [Validators.required]),
      storageType: new UntypedFormControl(null, [Validators.required]),
      sroMasterId: new UntypedFormControl(null, [Validators.required]),
      reportingYear: new UntypedFormControl(null, [Validators.required]),
      reportingQuarter: new UntypedFormControl(null, [Validators.required]),
      amountInvoiced: new UntypedFormControl('', [Validators.required, CommonUtils.greaterThanZeroValidator(), CommonUtils.isDecimalValidator()]),
      faicCode: new UntypedFormControl('', [Validators.required, Validators.minLength(10)]),
      employeeLastName: new UntypedFormControl('', [Validators.required, Validators.pattern(/^[a-zA-Z]+$/)])
    });
    this.xtosShipmentForm.get('reportingYear').valueChanges.pipe(takeWhile(() => this.observer$.value)).subscribe((val: Date) => {
      if (val instanceof Date) {
        this.xtosShipmentForm.get('reportingYear').setValue(val.getFullYear(), { emitEvent: false });
      }
    });
    this.dataSourceOrigin = new Observable((observer: any) => {
      this.ratesSroMasterService
        .getLocations(
          this.xtosShipmentForm.get('storageZipId').value
        )
        .subscribe((result: any) => {
          observer.next(result);
        });
    });
    this.xtosShipmentForm.get('uspsCity').valueChanges.pipe(filter(value => value))
      .subscribe(uspsCity => {
        const state = this.states.find(stateDetails => {
          return stateDetails.stateCountryCode2Digit === uspsCity.stateAbbrev;
        });
        this.xtosShipmentForm.get('city').setValue(uspsCity.cityName);
        this.xtosShipmentForm.get('state').setValue(uspsCity.stateAbbrev);
        this.xtosShipmentForm.get('zipCode').setValue(uspsCity.zipCode);
        this.xtosShipmentForm.get('stateId').setValue(state ? state.stateCountryId : undefined);
        this.xtosShipmentForm.get('storageStateId').setValue(state ? state.stateCountryId : undefined);
      });
  }

  changeTypeaheadOriginLoading(e: boolean): void {
    this.typeaheadOriginLoading = e;
  }

  typeaheadOriginOnSelect(e: TypeaheadMatch): void {
    this.xtosShipmentForm.get('uspsCity').setValue(e.item);
  }

  ngOnInit() {
    this.getSroCodes();
  }

  onReportingYearChange(event) {
    const year = new Date(event).getFullYear();
    this.printQtrList = CommonUtils.generateYearQuartersByYear(year);
  }

  getSroCodes() {
    this.sroCodes = [{sroMasterId: 91, sroId: 'GENRL'}, {sroMasterId: 86, sroId: 'XSARC'} ];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes['xtosShipment'] || !changes['xtosShipment'].currentValue) {
      return;
    }
    const xtosShipment = changes['xtosShipment'].currentValue as ExtendedStorage;
    xtosShipment.dateIntoExtraStorage = new Date(xtosShipment.dateIntoExtraStorage);
    xtosShipment.packingDate = new Date(xtosShipment.packingDate);
    this.xtosShipmentForm.patchValue(xtosShipment);
  }

  onPackingXtosDateChange(event) {
    console.log(event);
    const packingDate = moment(this.xtosShipmentForm.value.packingDate);
    const dateIntoExtraStorage = moment(event);
    if (packingDate.isValid() && dateIntoExtraStorage.isValid()) {
      this.showPackingDateError = !dateIntoExtraStorage.isSameOrAfter(packingDate);
    }
  }

  onBillingDateChange(event) {
    console.log(event);
    const packingDate = moment(this.xtosShipmentForm.value.packingDate);
    const dateIntoBilling = moment(event);
    if (packingDate.isValid() && dateIntoBilling.isValid()) {
      this.showBillingDateError = !dateIntoBilling.isSameOrAfter(packingDate);
    }
  }

  addToList() {
    const xtosShipment = { ...this.xtosShipmentForm.getRawValue() } as ExtendedStorage;
    xtosShipment.hhgExtStorageShipmentId = null;
    this.saveXTOSShipment.emit(xtosShipment);
  }

  modify() {
    const xtosShipment = { ...this.xtosShipmentForm.getRawValue() } as ExtendedStorage;
    this.saveXTOSShipment.emit(xtosShipment);
  }

  delete() {
    const xtosShipment = { ...this.xtosShipmentForm.getRawValue() } as ExtendedStorage;
    this.deleteXTOSShipment.emit(xtosShipment);
  }

  ngOnDestroy(): void {
    this.observer$.next(false);
  }
}
